/**
 * Public Chat Page
 * 
 * Main landing page with conversational interface.
 * Replaces traditional marketing website.
 */

import React, { useState, useEffect } from 'react';
import { Box, Snackbar, Alert } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useSearchParams } from 'react-router';
import { PublicTopBar } from './components/PublicTopBar';
import { PublicChatContainer } from './components/PublicChatContainer';

export const PublicChatPage: React.FC = () => {
  const { classes } = useStyles();
  const [showAuthCard, setShowAuthCard] = useState<'signup' | 'login' | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [showSessionExpiredMessage, setShowSessionExpiredMessage] = useState(false);

  // Check for session expired parameter
  useEffect(() => {
    if (searchParams.get('session_expired') === 'true') {
      setShowSessionExpiredMessage(true);
      setShowAuthCard('login');
      // Remove the query parameter from URL
      searchParams.delete('session_expired');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleLoginClick = () => {
    setShowAuthCard('login');
  };

  const handleSignupClick = () => {
    setShowAuthCard('signup');
  };

  const handleShowAuthCard = (type: 'signup' | 'login' | null) => {
    setShowAuthCard(type);
  };

  return (
    <Box className={classes.page}>
      {/* Background */}
      <Box className={classes.background} />

      {/* Top Bar */}
      <PublicTopBar
        onLoginClick={handleLoginClick}
        onSignupClick={handleSignupClick}
      />

      {/* Main Content */}
      <Box className={classes.content}>
        <PublicChatContainer
          showAuthCard={showAuthCard}
          onShowAuthCard={handleShowAuthCard}
        />
      </Box>

      {/* Session Expired Notification */}
      <Snackbar
        open={showSessionExpiredMessage}
        autoHideDuration={8000}
        onClose={() => setShowSessionExpiredMessage(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ top: 80 }}
      >
        <Alert
          severity="info"
          variant="filled"
          onClose={() => setShowSessionExpiredMessage(false)}
        >
          Your session has expired. Please log in again to continue.
        </Alert>
      </Snackbar>
    </Box>
  );
};

const useStyles = makeStyles()((theme) => ({
  page: {
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    overflow: 'hidden',
    backgroundColor: theme.palette.backgrounds.main_new,
  },
  background: {
    display: 'none',
  },
  content: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    marginTop: 64, // TopBar height
    overflow: 'hidden',
  },
}));

export default PublicChatPage;

